import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  FC,
  PropsWithChildren,
} from 'react';
import { useLocation } from 'react-router-dom';
import { usePermissions } from 'react-admin';
import { history } from '~/App';
import { changelogPermissions, matchRecordPage } from '~/utils/recordPages';
import { removeDraftPostfix } from '~/hooks/draft';
import { useEditMode } from '~/hooks/useEditModeContext';
import { useGetChangelogData, useGetChangelogsData } from '~/hooks/useGetChangelogsData';
import { Changelog } from '~/types/Changelog';

interface ChangelogContextProps {
  isAvailable: boolean;
  canView: boolean;
  changelogs: Changelog[];
  isLoadingChangelogs: boolean;
  selectedId: number | undefined;
  selectedRecord: any;
  isDrawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  select: (changelogId: number) => void;
  exit: () => void;
  goToPrev: () => void;
  goToNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
}

const EMPTY_CHANGELOGS: Changelog[] = [];

const defaultContext: ChangelogContextProps = {
  isAvailable: false,
  canView: false,
  changelogs: EMPTY_CHANGELOGS,
  isLoadingChangelogs: false,
  selectedId: undefined,
  selectedRecord: undefined,
  isDrawerOpen: false,
  openDrawer: () => {},
  closeDrawer: () => {},
  select: () => {},
  exit: () => {},
  goToPrev: () => {},
  goToNext: () => {},
  hasPrev: false,
  hasNext: false,
};

const ChangelogContext = createContext<ChangelogContextProps>(defaultContext);

export const ChangelogProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const { editMode, setEditMode } = useEditMode();
  const { permissions } = usePermissions();
  const location = useLocation();

  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isListRequested, setListRequested] = useState(false);

  const page = matchRecordPage(location.pathname);
  const resource = page?.resource;
  const recordId = page?.id ? removeDraftPostfix(page.id) : undefined;

  const canList = !!page?.changelog && !!permissions?.includes(changelogPermissions(page).list);
  const canView = !!page?.changelog && !!permissions?.includes(changelogPermissions(page).view);

  useEffect(() => {
    if (editMode !== 'CHANGELOG') {
      setSelectedId(undefined);
    }
  }, [editMode]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    setListRequested(false);
    setSelectedId(undefined);
  }, [resource, recordId]);

  const {
    data: changelogsData,
    isLoading: isLoadingChangelogs,
    refetch: refetchChangelogs,
  } = useGetChangelogsData(resource!, recordId!, canList && isListRequested);
  const changelogs: Changelog[] = changelogsData ?? EMPTY_CHANGELOGS;

  const { data: selectedChangelog } = useGetChangelogData(
    resource!,
    recordId!,
    canView && !!selectedId,
    selectedId
  );

  // keepPreviousData may still hold the previous record's snapshot
  const snapshot = selectedChangelog?.data;
  const isSnapshotOfCurrentRecord = snapshot?.id === recordId;

  const openDrawer = useCallback(() => {
    if (isListRequested) {
      refetchChangelogs();
    } else {
      setListRequested(true);
    }
    setDrawerOpen(true);
  }, [isListRequested, refetchChangelogs]);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const isDraft = !!page?.isDraft;
  const tabPath = page?.tabPath;

  const select = useCallback(
    (changelogId: number) => {
      if (isDraft && resource && recordId) {
        history.push(`/${resource}/${recordId}${tabPath}`);
      }
      setSelectedId(changelogId);
      setEditMode('CHANGELOG');
      setDrawerOpen(false);
    },
    [isDraft, tabPath, resource, recordId, setEditMode]
  );

  const exit = useCallback(() => setEditMode('VIEW'), [setEditMode]);

  const currentIndex = changelogs.findIndex((item) => item.id === selectedId);
  const hasPrev = currentIndex >= 0 && currentIndex < changelogs.length - 1;
  const hasNext = currentIndex > 0;

  const goToPrev = useCallback(() => {
    setSelectedId((prevId) => {
      const index = changelogs.findIndex((item) => item.id === prevId);
      return index < changelogs.length - 1 ? changelogs[index + 1].id : prevId;
    });
  }, [changelogs]);

  const goToNext = useCallback(() => {
    setSelectedId((prevId) => {
      const index = changelogs.findIndex((item) => item.id === prevId);
      return index > 0 ? changelogs[index - 1].id : prevId;
    });
  }, [changelogs]);

  return (
    <ChangelogContext.Provider
      value={{
        isAvailable: canList,
        canView,
        changelogs,
        isLoadingChangelogs,
        selectedId,
        selectedRecord: selectedId && isSnapshotOfCurrentRecord ? snapshot : undefined,
        isDrawerOpen,
        openDrawer,
        closeDrawer,
        select,
        exit,
        goToPrev,
        goToNext,
        hasPrev,
        hasNext,
      }}
    >
      {children}
    </ChangelogContext.Provider>
  );
};

export const useChangelog = (): ChangelogContextProps => useContext(ChangelogContext);

/**
 * The record to render: a historical snapshot while in CHANGELOG mode, the live record otherwise.
 */
export const useChangelogRecord = <T,>(record: T): T => {
  const { editMode } = useEditMode();
  const { selectedRecord } = useChangelog();

  return editMode === 'CHANGELOG' && selectedRecord ? selectedRecord : record;
};
